import React, { useState, useEffect } from 'react';
import {
    Save,
    Shield,
    Clock,
    Calendar,
    Settings as SettingsIcon,
    RefreshCw,
    Info,
    AlertTriangle,
    CheckCircle2,
    Globe
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import { toast } from 'react-hot-toast';

const GlobalLeaveConfiguration = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        globalMonthlyLeaveCap: 0,
        restrictNoticePeriod: true,
        allowNegativeBalance: false,
        enableLateDeduction: false,
        lateThreshold: 2,
        lateDeductionDays: 0.5
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/leave-management/settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.success && result.data) {
                setSettings({
                    globalMonthlyLeaveCap: result.data.globalMonthlyLeaveCap || 0,
                    restrictNoticePeriod: result.data.restrictNoticePeriod ?? true,
                    allowNegativeBalance: result.data.allowNegativeBalance ?? false,
                    enableLateDeduction: result.data.enableLateDeduction ?? false,
                    lateThreshold: result.data.lateThreshold ?? 2,
                    lateDeductionDays: result.data.lateDeductionDays ?? 0.5
                });
            }
        } catch (error) {
            console.error('Fetch settings error:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = type === 'checkbox' ? checked : value;

        if (name === 'globalMonthlyLeaveCap' || name === 'lateThreshold') {
            const numValue = parseInt(value) || 0;
            finalValue = Math.min(31, Math.max(0, numValue));
        }

        if (name === 'lateDeductionDays') {
            const numValue = parseFloat(value) || 0;
            finalValue = Math.min(1, Math.max(0, numValue));
        }

        setSettings(prev => ({ ...prev, [name]: finalValue }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/leave-management/settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(settings)
            });
            const result = await response.json();
            if (result.success) {
                toast.success('Global configurations updated successfully');
            } else {
                toast.error(result.message || 'Failed to update settings');
            }
        } catch (error) {
            console.error('Save settings error:', error);
            toast.error('Internal server error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
                        <Globe className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Global Leave Configuration</h1>
                        <p className="text-sm sm:text-base text-slate-500 mt-1">Manage system-wide attendance policies and late arrival penalties</p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={fetchSettings}
                        className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                    >
                        <RefreshCw className="h-4 w-4" />
                        <span>Refresh</span>
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {saving ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-300 border-t-slate-700" /> : <Save className="h-4 w-4" />}
                        <span>Save Configuration</span>
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column: Core Settings */}
                <div className="lg:col-span-2 space-y-6">
                    {/* General Policies Card */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                                <Shield className="h-5 w-5 text-gray-400" />
                                <h2 className="text-lg font-semibold text-gray-900">General Policies</h2>
                            </div>
                        </div>
                        <div className="p-6 space-y-6">
                            {/* Monthly Cap */}
                            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                                <div className="space-y-1">
                                    <h4 className="text-sm font-bold text-gray-900 flex items-center">
                                        <Calendar className="h-4 w-4 mr-2 text-brand" />
                                        Overall Monthly Leave Cap
                                    </h4>
                                    <p className="text-xs text-gray-500">Maximum leave days allowed per month (0 to disable)</p>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <input
                                        type="number"
                                        name="globalMonthlyLeaveCap"
                                        value={settings.globalMonthlyLeaveCap}
                                        onChange={handleInputChange}
                                        className="w-24 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-0 focus:border-brand focus:outline-none"
                                        min="0"
                                        max="31"
                                        step="1"
                                    />
                                    <span className="text-xs font-semibold text-gray-400 uppercase">Days</span>
                                </div>
                            </div>

                            {/* Notice Period */}
                            <div className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded-lg">
                                <div className="space-y-1">
                                    <h4 className="text-sm font-bold text-gray-900 flex items-center">
                                        <SettingsIcon className="h-4 w-4 mr-2 text-indigo-500" />
                                        Strict Notice Period Enforcement
                                    </h4>
                                    <p className="text-xs text-gray-500">Enable validation for leave notice requirements</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="restrictNoticePeriod"
                                        checked={settings.restrictNoticePeriod}
                                        onChange={handleInputChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Late Penalty Card */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
                            <div className="flex items-center space-x-2">
                                <Clock className="h-5 w-5 text-gray-400" />
                                <h2 className="text-lg font-semibold text-gray-900">Late Attendance Policy</h2>
                            </div>
                            <div className="flex items-center">
                                <span className={`text-[10px] font-bold uppercase tracking-wider mr-3 ${settings.enableLateDeduction ? 'text-green-600' : 'text-gray-400'}`}>
                                    {settings.enableLateDeduction ? 'Active' : 'Inactive'}
                                </span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="enableLateDeduction"
                                        checked={settings.enableLateDeduction}
                                        onChange={handleInputChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
                                </label>
                            </div>
                        </div>
                        <div className={`p-6 transition-all ${settings.enableLateDeduction ? 'opacity-100' : 'opacity-40 grayscale pointer-events-none bg-gray-50/30'}`}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Threshold */}
                                <div className="space-y-3">
                                    <label className="text-xs font-semibold text-gray-900 uppercase ml-1">Monthly Grace Days</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="lateThreshold"
                                            value={settings.lateThreshold}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-0 focus:border-brand focus:outline-none pr-12"
                                            min="0"
                                            max="31"
                                            step="1"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">Days</span>
                                    </div>
                                    <p className="text-[10px] text-gray-500 italic">No penalty up to this limit</p>
                                </div>

                                {/* Deduction Amount */}
                                <div className="space-y-3">
                                    <label className="text-xs font-semibold text-gray-900 uppercase ml-1">Deduction (Per Late)</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            name="lateDeductionDays"
                                            value={settings.lateDeductionDays}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-0 focus:border-brand focus:outline-none pr-16"
                                            min="0"
                                            max="1"
                                            step="0.1"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400 uppercase">Salary Day</span>
                                    </div>
                                    <p className="text-[10px] text-gray-500 italic">Penalty per day above grace days</p>
                                </div>
                            </div>

                            {/* Help Box */}
                            <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-lg flex items-start">
                                <Info className="h-4 w-4 text-blue-500 mr-3 mt-0.5 shrink-0" />
                                <div className="text-xs text-[#0B2033] leading-relaxed">
                                    <span className="font-bold">Logic Summary:</span> System identifies lates based on shift's grace check-in time.
                                    After <span className="font-bold">{settings.lateThreshold}</span> free lates, every subsequent late arrival will deduct <span className="font-bold">{settings.lateDeductionDays}</span> day's salary from monthly payroll.

                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Information */}
                <div className="space-y-6">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center">
                            <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                            Policy Insights
                        </h3>
                        <div className="space-y-4">
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-tighter mb-1">Impact</p>
                                <p className="text-xs text-gray-700 font-medium">Global settings apply to all active employees and override policy-specific caps.</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-tighter mb-1">Reset Rule</p>
                                <p className="text-xs text-gray-700 font-medium">Attendance-based late counters reset on the 1st of every calendar month.</p>
                            </div>
                            <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-tighter mb-1">Priority</p>
                                <p className="text-xs text-gray-700 font-medium italic">Approved Leave / Half-Day {'>'} Late Penalty.</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#0246B6] rounded-lg p-6 text-white shadow-lg relative overflow-hidden">
                        <div className="relative z-10">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400">Current Status</h4>
                                <CheckCircle2 className="h-4 w-4 text-green-400" />
                            </div>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-400">Deduction Mode</span>
                                    <span className="font-bold">{settings.enableLateDeduction ? 'Active (LOP)' : 'Inactive'}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-400">Monthly Limit</span>
                                    <span className="font-bold">{settings.globalMonthlyLeaveCap || 'Unlimited'}</span>
                                </div>
                            </div>
                        </div>
                        <div className="absolute -bottom-4 -right-4 h-24 w-24 bg-brand/10 rounded-full blur-2xl"></div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GlobalLeaveConfiguration;
